import { Box, Container } from "@mui/material";
import VideoBackgroundContainer from "components/shared/VideoBackgroundContainer";
import { usePageContext } from "contexts/PageContextProvider";
import LazyLoad from "react-lazy-load";
import ConditionalWrapper from "./ConditionalWrapper";

/**
 * * Page section with a background and centered content div
 * @param {Object} props
 * @param {string} props.id
 * @param {Object} [props.sx=null]
 * @param {Object} [props.backgroundSx=null]
 * @param {string} [props.maxContainerWidth="lg"]
 * @param {string} [props.backgroundVideoSrc]
 * @param {React.ReactNode} props.children
 * @returns {React.ReactElement} PageSection
 */
export default function PageSection({
  id,
  sx = null,
  backgroundSx = null,
  maxContainerWidth = "lg",
  backgroundVideoSrc,
  children,
}) {
  if (!id) {
    throw new Error("PageSection requires an id prop");
  }
  const { lazyLoadOffset } = usePageContext();
  return (
    <Box data-testid={id} id={id} sx={backgroundSx}>
      {/* Conditionally render background video container */}
      <ConditionalWrapper
        condition={backgroundVideoSrc}
        wrapper={(children) => (
          <VideoBackgroundContainer src={backgroundVideoSrc}>
            {children}
          </VideoBackgroundContainer>
        )}
      >
        <Box
          py={{ xs: 5, md: 10, lg: 15, xl: 20 }}
          px={{ xs: 2, sm: 5, md: 10, lg: 15 }}
          sx={sx}
        >
          {/* Lazily load section content, 
          does not render on test (see mock PageContextProvider) */}
          <ConditionalWrapper
            condition={lazyLoadOffset}
            wrapper={(children) => <LazyLoad>{children}</LazyLoad>}
          >
            <Container maxWidth={maxContainerWidth}>{children}</Container>
          </ConditionalWrapper>
        </Box>
      </ConditionalWrapper>
    </Box>
  );
}
